import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { isEmpty } from "lodash";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "zustand";

import { CREATE_INSIGHT_TRIGGER_TEXT } from "../../../../config/insights.config";
import { NOT_APPLICABLE_CARD_DETAILS } from "../../../../constants/common";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import { useContentCardShown } from "../../../../hooks/misc/useContentCardShown";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { navigationStore } from "../../../../store/navigation";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import type { CardDetail } from "../../../../types/card";
import { getPhoenixContentInfoFromStore } from "../../../../utils/phoenix.utils";
import { isExploreWithAITab } from "../../../../utils/tab.utils";
import {
    logWithTakt,
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
} from "../../../../utils/telemetry.utils";
import { getSearchParams } from "../../../../utils/url.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../commonStyles";
import { CardLayout } from "../insightDetails/CardLayout";

export interface OnDemandInsightsCardProps {
    template: TemplateAssociations;
    index?: number;
    summary?: string;
    additionalCardDetails?: CardDetail[];
    tag?: string;
}

export const OnDemandInsightCard: React.FunctionComponent<
    OnDemandInsightsCardProps
> = ({ template, index, summary, additionalCardDetails = [], tag }) => {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const activeTab = useStore(navigationStore, (state) => state.activeTab);
    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const insightsData = filters[contentSubType.provider].data;

    const navigate = useNavigate();
    const details: CardDetail[] = [
        {
            label: "Subject",
            value: isEmpty(template.program?.themes)
                ? NOT_APPLICABLE_CARD_DETAILS
                : template.program?.themes!.join(", "),
        },
        {
            label: "Topic",
            value: template.program?.name ?? NOT_APPLICABLE_CARD_DETAILS,
            lineClamp: 2,
        },
        {
            label: "Language",
            value: isEmpty(template.language)
                ? NOT_APPLICABLE_CARD_DETAILS
                : template.language,
            lineClamp: 2,
        },
        ...additionalCardDetails,
    ];

    useContentCardShown({
        insightType: InsightType.CDB_TEMPLATE,
        identifier: template.id,
    });

    const handleClick = useCallback(() => {
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK, {
            ...filters,
            identifier: template.id,
            resultPosition: index,
            insightType: InsightType.CDB_TEMPLATE,
            totalResults: insightsData.length,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    InsightType.CDB_TEMPLATE,
                    template.id,
                ),
                customData: {
                    filters,
                    resultPosition: index,
                    totalResults: insightsData.length,
                },
            },
        );
        navigate(
            `/domains/${template.domain!.id}/templates/${template.id}${getSearchParams({}, ["getPayloadFromInsight"])}`,
        );
    }, [insightsData, template, index]);

    const OnDemandInsightCardAction = () => {
        return (
            <StyledActionsContainer>
                <TextButton onClick={() => void handleClick()}>
                    {CREATE_INSIGHT_TRIGGER_TEXT}
                </TextButton>
                {isExploreWithAITab(activeTab) && template.id && (
                    <AquilaThumbsFeedback
                        placement={AquilaThumbsFeedbackPlacement.CARD}
                        identifier={template.id}
                        insightType={InsightType.CDB_TEMPLATE}
                        configId={"G4sThumbsFeedbackConfig_StormV4"}
                    />
                )}
            </StyledActionsContainer>
        );
    };

    return (
        <CardLayout
            dataTestId={template.id!}
            description={template.name ?? ""}
            details={details}
            actions={<OnDemandInsightCardAction />}
            summary={summary}
            tag={tag}
        />
    );
};
